// One poller per signed-in workspace. Live measurements are never cached
// as proof, and leave the screen when a request fails or identity changes.
import { createContext, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from 'react'
import { asLiveChecks, localChecks, localRunner } from '../api/shellLocalReads'
import type { Api, LiveCheck } from '../api/types'
import { useBoardState } from '../store/context'
import './progress.css'

const EMPTY: readonly LiveCheck[] = []
class ProgressSource {
  private entries: readonly LiveCheck[] = EMPTY
  private listeners = new Set<() => void>()
  private timer: ReturnType<typeof setInterval> | null = null
  private generation = 0
  private asking = false
  constructor(private read: () => Promise<LiveCheck[]>) {}
  snapshot = () => this.entries
  private set(entries: readonly LiveCheck[]) {
    this.entries = entries
    for (const listener of this.listeners) listener()
  }
  private poll = async () => {
    if (this.asking || document.visibilityState === 'hidden') return
    this.asking = true
    const generation = this.generation
    try {
      const entries = await this.read()
      if (generation === this.generation) this.set(entries)
    } catch {
      if (generation === this.generation) this.set(EMPTY)
    } finally { this.asking = false }
  }
  private visible = () => { void this.poll() }
  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    if (this.listeners.size === 1) {
      void this.poll()
      this.timer = setInterval(() => { void this.poll() }, 2000)
      document.addEventListener('visibilitychange', this.visible)
    }
    return () => {
      this.listeners.delete(listener)
      if (this.listeners.size === 0) this.dispose()
    }
  }
  dispose = () => {
    this.generation++
    if (this.timer) clearInterval(this.timer)
    this.timer = null
    document.removeEventListener('visibilitychange', this.visible)
    this.entries = EMPTY
  }
}
const Context = createContext<ProgressSource | null>(null)
const quiet = () => () => {}
const nothing = () => EMPTY

/**
 * Online, the server's live checks. Offline, inside the Mac app, this Mac's
 * runner's own checks from its local reads (Poulpe-828), their project and
 * session from the jobs the device kept; none when that runner gives nothing.
 */
export function CheckProgressProvider({ api, children }: { api: Partial<Pick<Api, 'checkProgress'>>; children: ReactNode }) {
  const { me, offline, status, jobs } = useBoardState()
  const member = me?.member?.id
  const workspace = me?.workspace.id
  const read = api.checkProgress
  const jobsNow = useRef(jobs)
  jobsNow.current = jobs
  const local = offline && status !== 'signed_out' && localRunner() !== null
  const source = useMemo(() => read && !offline && status !== 'signed_out'
    ? new ProgressSource(() => read(member))
    : local ? new ProgressSource(async () => asLiveChecks(await localChecks() ?? [], jobsNow.current)) : null,
  [read, member, workspace, offline, status, local])
  useEffect(() => () => source?.dispose(), [source])
  return <Context.Provider value={source}>{children}</Context.Provider>
}

function progressState(check: LiveCheck) {
  const progress = check.progress
  if (!progress) return 'waiting'
  switch (progress.activity?.phase) {
    case 'queued': case 'admitted': return 'waiting'
    case 'preparing': case 'lease_acquired': return 'preparing'
    case 'part_ended': return 'finishing'
    case 'finished': return 'finished'
    default: return 'running'
  }
}

const time = (ms: number) => ms < 60_000 ? `${Math.floor(ms / 1000)} s` : `${Math.floor(ms / 60_000)} min`

/** A holder is a hint from a live report, displayed as bounded plain text. */
function holderText(holder: string | null): string {
  const text = (holder ?? '').replace(/\s+/g, ' ').replace(/[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g, '').trim()
  // Keep the end: legacy holders start with a long path, then give pid/time.
  return text.length > 160 ? `…${text.slice(-159)}` : text
}

export function progressText(check: LiveCheck, now: number): string {
  const progress = check.progress
  if (!progress) return 'Waiting for its turn'
  const since = check.reported_at ? Math.max(0, now - Date.parse(check.reported_at)) : 0
  const delta = Number.isFinite(since) ? since : 0
  const activity = progress.activity
  const state = progressState(check)
  const waiting = state === 'waiting'
  const elapsed = activity
    ? Math.max(0, activity.execution_ms + (!waiting && state !== 'finished' ? delta : 0))
    : Math.max(0, progress.elapsed_ms + delta)
  const words: string[] = []
  if (activity) {
    const labels = { queued: 'Waiting for its turn', admitted: 'Waiting for this Mac', preparing: 'Preparing', lease_acquired: 'Preparing', part_started: 'Running', part_ended: progress.completed < progress.total ? 'Finishing part' : 'Finishing', finished: 'Finished' }
    const purposes = { landing: 'Landing check', proposal: 'Proposal check', hand: 'Requested check', idle_main: 'Main check' }
    words.push(labels[activity.phase], purposes[activity.purpose])
    words.push(`waited ${time(Math.max(0, activity.wait_ms + (waiting ? delta : 0)))}`, `executed ${time(elapsed)}`)
  } else words.push('Running', time(elapsed))
  if (progress.total > 0) words.splice(activity ? 2 : 1, 0, `${progress.completed}/${progress.total} ${progress.total === 1 ? 'part' : 'parts'}`)
  if (progress.part && (!activity || activity.phase === 'part_started' || activity.phase === 'part_ended')) words.push(progress.part)
  if ((!activity || activity.phase === 'part_started') && progress.command_total !== null && progress.command_total > 0 && progress.command_completed !== null) {
    words.push(`${progress.command_completed}/${progress.command_total} (${Math.floor(100 * progress.command_completed / progress.command_total)}%)`)
  }
  if (check.usual_ms !== null && check.usual_ms > 0) {
    words.push(!waiting && elapsed > check.usual_ms ? 'Longer than usual' : `usual ${time(check.usual_ms)}`)
  }
  // The urgency its runner applies to its place (Poulpe-680).
  if (activity?.urgency === 'urgent') words.push('Urgent')
  if (activity && waiting) {
    const holder = holderText(activity.holder)
    if (holder) words.push(`Current check: ${holder}`)
  }
  return words.join(' · ')
}

/** Select by job whenever known; server links also cover a session's hand
 * checks and a candidate whose landing check has just been queued. */
export function useLiveChecks() {
  const source = useContext(Context)
  return useSyncExternalStore(source?.subscribe ?? quiet, source?.snapshot ?? nothing)
}

export function LiveChecks({ job, session, proposal, landing }: { job?: string | null; session?: string; proposal?: string; landing?: string }) {
  const entries = useLiveChecks()
  const checks = entries.filter(check => job ? check.job === job : session ? check.session === session : landing ? check.landing === landing : proposal ? check.proposal === proposal : false)
  const [now, setNow] = useState(Date.now)
  const running = checks.some(check => check.progress !== null && progressState(check) !== 'finished')
  useEffect(() => {
    if (!running) return
    setNow(Date.now())
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [running])
  if (!checks.length) return null
  return <span className="live-checks" aria-label="Check progress">{checks.map(check => <span key={check.job} className="live-check" data-state={progressState(check)}>{progressText(check, now)}</span>)}</span>
}
